import rough from "roughjs/bin/rough";

import {
  type GlobalPoint,
  isRightAngleRads,
  lineSegment,
  pointFrom,
  pointRotateRads,
  type Radians,
} from "@excalidraw/math";

import {
  BOUND_TEXT_PADDING,
  DEFAULT_REDUCED_GLOBAL_ALPHA,
  ELEMENT_READY_TO_ERASE_OPACITY,
  FRAME_STYLE,
  DARK_THEME_FILTER,
  MIME_TYPES,
  THEME,
  distance,
  getFontString,
  isRTL,
  getVerticalOffset,
  invariant,
  applyDarkModeFilter,
  isSafari,
  isTransparent,
} from "@excalidraw/common";

import type {
  AppState,
  StaticCanvasAppState,
  Zoom,
  InteractiveCanvasAppState,
  ElementsPendingErasure,
  PendingExcalidrawElements,
  NormalizedZoomValue,
} from "@excalidraw/excalidraw/types";

import type {
  StaticCanvasRenderConfig,
  RenderableElementsMap,
  InteractiveCanvasRenderConfig,
} from "@excalidraw/excalidraw/scene/types";

import {
  formatCounterValue,
  getAnimationColors,
  getAnimationCounter,
  getAnimationEmitter,
  getAnimationFlow,
  getAnimationFragment,
  getAnimationGradientDelta,
  getAnimationMorph,
  getAnimationRough,
  getAnimationTextPath,
  getAnimationOpacity,
  getAnimationOverride,
  getAnimationStroke,
  getAnimationTextEffect,
} from "./animationOverrides";
import { getFragmentCells } from "./fragments";
import { CONFETTI_COLORS, EMIT_KINDS, getEmitterParticles } from "./particles";
import {
  buildTextUnits,
  getTextCharState,
  layoutTextOnPath,
} from "./textEffects";
import { getRoughTextJitter } from "./roughText";

import {
  elementOutlinePolygon,
  morphShapeForElementType,
  sampleMorphShape,
  sampleOutlinePoints,
} from "./morphShapes";
import { getElementAbsoluteCoords, getElementBounds } from "./bounds";
import { getUncroppedImageElement } from "./cropElement";
import { LinearElementEditor } from "./linearElementEditor";
import {
  getBoundTextElement,
  getContainerCoords,
  getContainerElement,
  getBoundTextMaxHeight,
  getBoundTextMaxWidth,
} from "./textElement";
import { getLineHeightInPx } from "./textMeasurements";
import {
  isTextElement,
  isLinearElement,
  isFreeDrawElement,
  isInitializedImageElement,
  isArrowElement,
  hasBoundTextElement,
  isMagicFrameElement,
  isImageElement,
} from "./typeChecks";
import { getContainingFrame } from "./frame";
import { getCornerRadii, isPathALoop } from "./utils";
import { applyGradientDelta, buildCanvasGradient } from "./gradient";

import { ShapeCache, getPartialFreedrawOutlinePoints } from "./shape";
import {
  applyEffectsToCanvas,
  getEffectsPadding,
  hasEnabledEffects,
  isCanvasFilterSupported,
  renderFrameSurface,
} from "./effects";
import { getFlattenedPathPoints } from "./pen/flatten";
import { buildPenPathD } from "./pen/pathGeometry";
import { isPenPath } from "./pen/types";
import {
  applyEraserMasks,
  getBrushPadding,
  hashString,
  isBrushElement,
  renderBrushStroke,
} from "./brush";

import type { FragmentConfig } from "./fragments";
import type { EmitterConfig } from "./particles";

import type {
  ExcalidrawElement,
  ExcalidrawTextElement,
  NonDeletedExcalidrawElement,
  ExcalidrawFreeDrawElement,
  ExcalidrawLineElement,
  ExcalidrawImageElement,
  ExcalidrawTextElementWithContainer,
  ExcalidrawFrameLikeElement,
  NonDeletedSceneElementsMap,
  ElementsMap,
} from "./types";

import type { RoughCanvas } from "roughjs/bin/canvas";

const isPendingImageElement = (
  element: ExcalidrawElement,
  renderConfig: StaticCanvasRenderConfig,
) =>
  isInitializedImageElement(element) &&
  !renderConfig.imageCache.has(element.fileId);

const getCanvasPadding = (element: ExcalidrawElement) => {
  const base = (() => {
    switch (element.type) {
      case "freedraw":
        return isBrushElement(element)
          ? getBrushPadding(element)
          : element.strokeWidth * 12;
      case "text":
        // per-char effects move glyphs well outside the text bbox (e.g. drop-in
        // falls from 1.5× fontSize above) — pad the cache canvas accordingly
        return getAnimationTextEffect(element.id)
          ? element.fontSize * 2
          : element.fontSize / 2;
      case "arrow":
        if (element.startArrowhead || element.endArrowhead) {
          return 40;
        }
        return 20;
      default:
        return 20;
    }
  })();
  return base + getEffectsPadding(element);
};

export const getRenderOpacity = (
  element: ExcalidrawElement,
  containingFrame: ExcalidrawFrameLikeElement | null,
  elementsPendingErasure: ElementsPendingErasure,
  pendingNodes: Readonly<PendingExcalidrawElements> | null,
  globalAlpha: number = 1,
) => {
  // multiplying frame opacity with element opacity to combine them
  // (e.g. frame 50% and element 50% opacity should result in 25% opacity)
  let opacity =
    (((containingFrame?.opacity ?? 100) * element.opacity) / 10000) *
    globalAlpha;

  // if pending erasure, multiply again to combine further
  // (so that erasing always results in lower opacity than original)
  if (
    elementsPendingErasure.has(element.id) ||
    (pendingNodes && pendingNodes.some((node) => node.id === element.id)) ||
    (containingFrame && elementsPendingErasure.has(containingFrame.id))
  ) {
    opacity *= ELEMENT_READY_TO_ERASE_OPACITY / 100;
  }

  return opacity;
};

export interface ExcalidrawElementWithCanvas {
  element: ExcalidrawElement | ExcalidrawTextElement;
  canvas: HTMLCanvasElement;
  theme: AppState["theme"];
  scale: number;
  angle: number;
  zoomValue: AppState["zoom"]["value"];
  canvasOffsetX: number;
  canvasOffsetY: number;
  boundTextElementVersion: number | null;
  imageCrop: ExcalidrawImageElement["crop"] | null;
  containingFrameOpacity: number;
  boundTextCanvas: HTMLCanvasElement;
  /** textProgress of the animation override the canvas was generated with —
   * regenerates the cache each tick while a per-char text effect plays */
  textAnimProgress: number | null;
  /** dashOffset|drawProgress the canvas was generated with — regenerates the
   * cache each tick while a stroke animation (flow / line draw) plays */
  strokeAnimState: string | null;
}

const cappedElementCanvasSize = (
  element: NonDeletedExcalidrawElement,
  elementsMap: ElementsMap,
  zoom: Zoom,
): {
  width: number;
  height: number;
  scale: number;
} => {
  // these limits are ballpark, they depend on specific browsers and device.
  // We've chosen lower limits to be safe. We might want to change these limits
  // based on browser/device type, if we get reports of low quality rendering
  // on zoom.
  //
  // ~ safari mobile canvas area limit
  const AREA_LIMIT = 16777216;
  // ~ safari width/height limit based on developer.mozilla.org.
  const WIDTH_HEIGHT_LIMIT = 32767;

  const padding = getCanvasPadding(element);

  const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
  const elementWidth =
    isLinearElement(element) || isFreeDrawElement(element)
      ? distance(x1, x2)
      : element.width;
  const elementHeight =
    isLinearElement(element) || isFreeDrawElement(element)
      ? distance(y1, y2)
      : element.height;

  let width = elementWidth * window.devicePixelRatio + padding * 2;
  let height = elementHeight * window.devicePixelRatio + padding * 2;

  let scale: number = zoom.value;

  // rescale to ensure width and height is within limits
  if (
    width * scale > WIDTH_HEIGHT_LIMIT ||
    height * scale > WIDTH_HEIGHT_LIMIT
  ) {
    scale = Math.min(WIDTH_HEIGHT_LIMIT / width, WIDTH_HEIGHT_LIMIT / height);
  }

  // rescale to ensure canvas area is within limits
  if (width * height * scale * scale > AREA_LIMIT) {
    scale = Math.sqrt(AREA_LIMIT / (width * height));
  }

  width = Math.floor(width * scale);
  height = Math.floor(height * scale);

  return { width, height, scale };
};

/** True once zoom pushes the per-element cache bitmap past its safe
 * resolution ceiling (cappedElementCanvasSize's AREA_LIMIT/WIDTH_HEIGHT_LIMIT)
 * — i.e. the cache can no longer keep up with zoom.value. Callers use this to
 * fall back to drawing straight onto the visible (already zoom-scaled)
 * canvas instead of blitting an under-resolved bitmap, so edges stay crisp
 * (vector-sharp) at any zoom instead of blurring or pixelating. */
const isElementCanvasResolutionCapped = (
  element: NonDeletedExcalidrawElement,
  elementsMap: ElementsMap,
  zoom: Zoom,
): boolean =>
  cappedElementCanvasSize(element, elementsMap, zoom).scale < zoom.value;

const generateElementCanvas = (
  element: NonDeletedExcalidrawElement,
  elementsMap: NonDeletedSceneElementsMap,
  zoom: Zoom,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState | InteractiveCanvasAppState,
): ExcalidrawElementWithCanvas | null => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d")!;
  const padding = getCanvasPadding(element);

  const { width, height, scale } = cappedElementCanvasSize(
    element,
    elementsMap,
    zoom,
  );

  if (!width || !height) {
    return null;
  }

  canvas.width = width;
  canvas.height = height;

  let canvasOffsetX = -100;
  let canvasOffsetY = 0;

  if (isLinearElement(element) || isFreeDrawElement(element)) {
    const [x1, y1] = getElementAbsoluteCoords(element, elementsMap);

    canvasOffsetX =
      element.x > x1
        ? distance(element.x, x1) * window.devicePixelRatio * scale
        : 0;

    canvasOffsetY =
      element.y > y1
        ? distance(element.y, y1) * window.devicePixelRatio * scale
        : 0;

    context.translate(canvasOffsetX, canvasOffsetY);
  }

  context.save();
  context.translate(padding * scale, padding * scale);
  context.scale(
    window.devicePixelRatio * scale,
    window.devicePixelRatio * scale,
  );

  const rc = rough.canvas(canvas);

  drawElementOnCanvas(element, rc, context, renderConfig);

  context.restore();

  applyEffectsToCanvas(
    canvas,
    element,
    window.devicePixelRatio * scale,
    renderConfig.imageCache,
  );

  const boundTextElement = getBoundTextElement(element, elementsMap);
  const boundTextCanvas = document.createElement("canvas");
  const boundTextCanvasContext = boundTextCanvas.getContext("2d")!;

  if (isArrowElement(element) && boundTextElement) {
    const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
    // Take max dimensions of arrow canvas so that when canvas is rotated
    // the arrow doesn't get clipped
    const maxDim = Math.max(distance(x1, x2), distance(y1, y2));
    boundTextCanvas.width =
      maxDim * window.devicePixelRatio * scale + padding * scale * 10;
    boundTextCanvas.height =
      maxDim * window.devicePixelRatio * scale + padding * scale * 10;
    boundTextCanvasContext.translate(
      boundTextCanvas.width / 2,
      boundTextCanvas.height / 2,
    );
    boundTextCanvasContext.rotate(element.angle);
    boundTextCanvasContext.drawImage(
      canvas!,
      -canvas.width / 2,
      -canvas.height / 2,
      canvas.width,
      canvas.height,
    );

    const [, , , , boundTextCx, boundTextCy] = getElementAbsoluteCoords(
      boundTextElement,
      elementsMap,
    );

    boundTextCanvasContext.rotate(-element.angle);
    const offsetX = (boundTextCanvas.width - canvas!.width) / 2;
    const offsetY = (boundTextCanvas.height - canvas!.height) / 2;
    const shiftX =
      boundTextCanvas.width / 2 -
      (boundTextCx - x1) * window.devicePixelRatio * scale -
      offsetX -
      padding * scale;

    const shiftY =
      boundTextCanvas.height / 2 -
      (boundTextCy - y1) * window.devicePixelRatio * scale -
      offsetY -
      padding * scale;
    boundTextCanvasContext.translate(-shiftX, -shiftY);
    // Clear the bound text area
    boundTextCanvasContext.clearRect(
      -(boundTextElement.width / 2 + BOUND_TEXT_PADDING) *
        window.devicePixelRatio *
        scale,
      -(boundTextElement.height / 2 + BOUND_TEXT_PADDING) *
        window.devicePixelRatio *
        scale,
      (boundTextElement.width + BOUND_TEXT_PADDING * 2) *
        window.devicePixelRatio *
        scale,
      (boundTextElement.height + BOUND_TEXT_PADDING * 2) *
        window.devicePixelRatio *
        scale,
    );
  }

  return {
    element,
    canvas,
    theme: appState.theme,
    scale,
    zoomValue: zoom.value,
    canvasOffsetX,
    canvasOffsetY,
    boundTextElementVersion:
      getBoundTextElement(element, elementsMap)?.version || null,
    containingFrameOpacity:
      getContainingFrame(element, elementsMap)?.opacity || 100,
    boundTextCanvas,
    angle: element.angle,
    imageCrop: isImageElement(element) ? element.crop : null,
    textAnimProgress: getAnimationOverride(element.id)?.textProgress ?? null,
    strokeAnimState: getStrokeAnimState(element.id),
  };
};

export const DEFAULT_LINK_SIZE = 14;

const IMAGE_PLACEHOLDER_IMG =
  typeof document !== "undefined"
    ? document.createElement("img")
    : ({ src: "" } as HTMLImageElement); // mock image element outside of browser

IMAGE_PLACEHOLDER_IMG.src = `data:${MIME_TYPES.svg},${encodeURIComponent(
  `<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="image" class="svg-inline--fa fa-image fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#888" d="M464 448H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h416c26.51 0 48 21.49 48 48v288c0 26.51-21.49 48-48 48zM112 120c-30.928 0-56 25.072-56 56s25.072 56 56 56 56-25.072 56-56-25.072-56-56-56zM64 384h384V272l-87.515-87.515c-4.686-4.686-12.284-4.686-16.971 0L208 320l-55.515-55.515c-4.686-4.686-12.284-4.686-16.971 0L64 336v48z"></path></svg>`,
)}`;

const IMAGE_ERROR_PLACEHOLDER_IMG =
  typeof document !== "undefined"
    ? document.createElement("img")
    : ({ src: "" } as HTMLImageElement); // mock image element outside of browser

IMAGE_ERROR_PLACEHOLDER_IMG.src = `data:${MIME_TYPES.svg},${encodeURIComponent(
  `<svg viewBox="0 0 668 668" xmlns="http://www.w3.org/2000/svg" xml:space="preserve" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2"><path d="M464 448H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h416c26.51 0 48 21.49 48 48v288c0 26.51-21.49 48-48 48ZM112 120c-30.928 0-56 25.072-56 56s25.072 56 56 56 56-25.072 56-56-25.072-56-56-56ZM64 384h384V272l-87.515-87.515c-4.686-4.686-12.284-4.686-16.971 0L208 320l-55.515-55.515c-4.686-4.686-12.284-4.686-16.971 0L64 336v48Z" style="fill:#888;fill-rule:nonzero" transform="matrix(.81709 0 0 .81709 124.825 145.825)"/><path d="M256 8C119.034 8 8 119.033 8 256c0 136.967 111.034 248 248 248s248-111.034 248-248S392.967 8 256 8Zm130.108 117.892c65.448 65.448 70 165.481 20.677 235.637L150.47 105.216c70.204-49.356 170.226-44.735 235.638 20.676ZM125.892 386.108c-65.448-65.448-70-165.481-20.677-235.637L361.53 406.784c-70.203 49.356-170.226 44.736-235.638-20.676Z" style="fill:#888;fill-rule:nonzero" transform="matrix(.30366 0 0 .30366 506.822 60.065)"/></svg>`,
)}`;

const drawImagePlaceholder = (
  element: ExcalidrawImageElement,
  context: CanvasRenderingContext2D,
  theme: StaticCanvasRenderConfig["theme"],
) => {
  context.fillStyle = theme === THEME.DARK ? "#2E2E2E" : "#E7E7E7";
  context.fillRect(0, 0, element.width, element.height);

  const imageMinWidthOrHeight = Math.min(element.width, element.height);

  const size = Math.min(
    imageMinWidthOrHeight,
    Math.min(imageMinWidthOrHeight * 0.4, 100),
  );

  context.drawImage(
    element.status === "error"
      ? IMAGE_ERROR_PLACEHOLDER_IMG
      : IMAGE_PLACEHOLDER_IMG,
    element.width / 2 - size / 2,
    element.height / 2 - size / 2,
    size,
    size,
  );
};

/** Serialized appearance-animation state (dash / draw-on / colors / morph /
 * flow / gradient) used as a cache key component — any change regenerates the
 * canvas. */
const getStrokeAnimState = (elementId: string): string | null => {
  const stroke = getAnimationStroke(elementId);
  const colors = getAnimationColors(elementId);
  const morph = getAnimationMorph(elementId);
  const flow = getAnimationFlow(elementId);
  const rough = getAnimationRough(elementId);
  const counter = getAnimationCounter(elementId);
  const textPath = getAnimationTextPath(elementId);
  const gradient = getAnimationGradientDelta(elementId);
  if (
    !stroke &&
    !colors &&
    !morph &&
    !flow &&
    !rough &&
    !counter &&
    !textPath &&
    !gradient
  ) {
    return null;
  }
  return [
    stroke?.dashOffset ?? "",
    stroke?.dashLen ?? "",
    stroke?.dashGap ?? "",
    stroke?.drawProgress ?? "",
    stroke?.drawStart ?? "",
    colors?.stroke ?? "",
    colors?.background ?? "",
    morph
      ? `${morph.shape}:${morph.progress}:${morph.outline?.join(",") ?? ""}`
      : "",
    flow ? `${flow.phase}:${flow.count}:${flow.size}:${flow.color ?? ""}` : "",
    rough ? `${rough.seedShift}:${rough.roughnessDelta}` : "",
    counter ? formatCounterValue(counter) : "",
    textPath ? `${textPath.progress}:${textPath.outline.length}` : "",
    gradient
      ? `${gradient.angleDelta ?? ""}:${gradient.offsetShift ?? ""}:${
          gradient.stopColors?.join(",") ?? ""
        }`
      : "",
  ].join("|");
};

/** True when a fill-color animation is active on an element whose drawable
 * has no fill geometry (transparent background) — rough must regenerate it
 * with a placeholder fill for the animated color to have anything to paint.
 * Open lines/freedraw and arrows have no fillable region, animation or not. */
const needsAnimatedFillVariant = (
  element: NonDeletedExcalidrawElement,
): boolean => {
  if (!isTransparent(element.backgroundColor) || element.backgroundGradient) {
    return false;
  }
  if (getAnimationColors(element.id)?.background === undefined) {
    return false;
  }
  switch (element.type) {
    case "rectangle":
    case "iframe":
    case "embeddable":
    case "diamond":
    case "ellipse":
      return true;
    case "line":
    case "freedraw":
      return isPathALoop(element.points);
    default:
      return false;
  }
};

/** The element's rough drawable(s), regenerated with shifted seed/roughness
 * while a boil / sketch-in animation drives those channels, or with a
 * placeholder fill while a fill-color animation runs on a transparent
 * background. */
const animatedElementShape = <
  T extends Exclude<NonDeletedExcalidrawElement, { type: "selection" }>,
>(
  element: T,
  renderConfig: StaticCanvasRenderConfig,
) => {
  const rough = getAnimationRough(element.id);
  const solidFill = needsAnimatedFillVariant(element);
  return rough || solidFill
    ? ShapeCache.generateVariantShape(
        element,
        renderConfig,
        rough?.seedShift ?? 0,
        rough?.roughnessDelta ?? 0,
        solidFill,
      )
    : ShapeCache.generateElementShape(element, renderConfig);
};

/** Geometric (single-pass) stroke length, used to infer how many times
 * rough stroked the path. */
const geometricStrokeLength = (element: NonDeletedExcalidrawElement) => {
  if (isLinearElement(element) || isFreeDrawElement(element)) {
    let length = 0;
    for (let i = 1; i < element.points.length; i++) {
      const [x1, y1] = element.points[i - 1];
      const [x2, y2] = element.points[i];
      length += Math.hypot(x2 - x1, y2 - y1);
    }
    return Math.max(length, 1);
  }
  return Math.max(2 * (element.width + element.height), 1);
};

const subpathCountCache = new WeakMap<object, number>();

/** How many subpaths rough emitted for a drawable. Canvas restarts the dash
 * pattern at every subpath, so the dash-trick draw-on is only faithful when
 * each stroke pass stayed continuous (≤ 2 subpaths = at most one per pass).
 * Rounded lines/arrows (rough `curve`) do; sharp polylines and elbow arrows
 * get one subpath per segment per pass, where the dash window would restart
 * at every corner and reveal all segments at once. */
const subpathCount = (shape: import("roughjs/bin/core").Drawable): number => {
  const cached = subpathCountCache.get(shape);
  if (cached !== undefined) {
    return cached;
  }
  let moves = 0;
  for (const set of shape.sets as Array<{
    type: string;
    ops: Array<{ op: string }>;
  }>) {
    if (set.type !== "path") {
      continue;
    }
    for (const op of set.ops) {
      if (op.op === "move") {
        moves++;
      }
    }
  }
  subpathCountCache.set(shape, moves);
  return moves;
};

const drawableLengthCache = new WeakMap<object, number>();

/** Exact drawn stroke length of a rough drawable (walks its path ops,
 * sampling beziers) — the dash-trick draw-on budget must match the real
 * length, or draw/erase visibly stalls at the ends of the tween. */
const drawnPathLength = (
  shape: import("roughjs/bin/core").Drawable,
): number => {
  const cached = drawableLengthCache.get(shape);
  if (cached !== undefined) {
    return cached;
  }
  let total = 0;
  for (const set of shape.sets as Array<{
    type: string;
    ops: Array<{ op: string; data: number[] }>;
  }>) {
    if (set.type !== "path") {
      continue;
    }
    let x = 0;
    let y = 0;
    for (const op of set.ops) {
      if (op.op === "move") {
        [x, y] = op.data;
      } else if (op.op === "lineTo") {
        total += Math.hypot(op.data[0] - x, op.data[1] - y);
        [x, y] = op.data;
      } else if (op.op === "bcurveTo") {
        const [c1x, c1y, c2x, c2y, ex, ey] = op.data;
        let px = x;
        let py = y;
        for (let i = 1; i <= 8; i++) {
          const t = i / 8;
          const u = 1 - t;
          const bx =
            u * u * u * x +
            3 * u * u * t * c1x +
            3 * u * t * t * c2x +
            t ** 3 * ex;
          const by =
            u * u * u * y +
            3 * u * u * t * c1y +
            3 * u * t * t * c2y +
            t ** 3 * ey;
          total += Math.hypot(bx - px, by - py);
          px = bx;
          py = by;
        }
        x = ex;
        y = ey;
      }
    }
  }
  const length = Math.max(total, 1);
  drawableLengthCache.set(shape, length);
  return length;
};

const continuousLengthCache = new WeakMap<object, number>();

/** Length of a drawable's path ops walked as ONE continuous path — every
 * subpath jump ('move' after the first) counts as a straight connector,
 * matching what traceContinuousPath strokes. */
const continuousOpsLength = (
  shape: import("roughjs/bin/core").Drawable,
): number => {
  const cached = continuousLengthCache.get(shape);
  if (cached !== undefined) {
    return cached;
  }
  let total = 0;
  let x = 0;
  let y = 0;
  let started = false;
  for (const set of shape.sets as Array<{
    type: string;
    ops: Array<{ op: string; data: number[] }>;
  }>) {
    if (set.type !== "path") {
      continue;
    }
    for (const op of set.ops) {
      if (op.op === "move") {
        if (started) {
          total += Math.hypot(op.data[0] - x, op.data[1] - y);
        }
        [x, y] = op.data;
        started = true;
      } else if (op.op === "lineTo") {
        total += Math.hypot(op.data[0] - x, op.data[1] - y);
        [x, y] = op.data;
      } else if (op.op === "bcurveTo") {
        const [c1x, c1y, c2x, c2y, ex, ey] = op.data;
        let px = x;
        let py = y;
        for (let i = 1; i <= 8; i++) {
          const t = i / 8;
          const u = 1 - t;
          const bx =
            u * u * u * x +
            3 * u * u * t * c1x +
            3 * u * t * t * c2x +
            t ** 3 * ex;
          const by =
            u * u * u * y +
            3 * u * u * t * c1y +
            3 * u * t * t * c2y +
            t ** 3 * ey;
          total += Math.hypot(bx - px, by - py);
          px = bx;
          py = by;
        }
        x = ex;
        y = ey;
      }
    }
  }
  const length = Math.max(total, 1);
  continuousLengthCache.set(shape, length);
  return length;
};

/** Replays a drawable's path ops into the context as ONE continuous path —
 * subpath 'move's become lineTo connectors, so a canvas dash window travels
 * the whole outline instead of restarting at every rough subpath. */
const traceContinuousPath = (
  context: CanvasRenderingContext2D,
  shape: import("roughjs/bin/core").Drawable,
) => {
  let started = false;
  context.beginPath();
  for (const set of shape.sets as Array<{
    type: string;
    ops: Array<{ op: string; data: number[] }>;
  }>) {
    if (set.type !== "path") {
      continue;
    }
    for (const op of set.ops) {
      if (op.op === "move") {
        if (started) {
          context.lineTo(op.data[0], op.data[1]);
        } else {
          context.moveTo(op.data[0], op.data[1]);
          started = true;
        }
      } else if (op.op === "lineTo") {
        context.lineTo(op.data[0], op.data[1]);
      } else if (op.op === "bcurveTo") {
        context.bezierCurveTo(
          op.data[0],
          op.data[1],
          op.data[2],
          op.data[3],
          op.data[4],
          op.data[5],
        );
      }
    }
  }
};

/**
 * Draws a rough shape, applying any active stroke animation (marching dash
 * flow, dash-trick draw-on, animated colors) by temporarily overriding the
 * drawable's options. The drawable is shared via ShapeCache, so options are
 * restored right after the draw call.
 */
const drawShapeWithStrokeAnimation = (
  rc: RoughCanvas,
  shape: import("roughjs/bin/core").Drawable,
  element: NonDeletedExcalidrawElement,
  theme: StaticCanvasRenderConfig["theme"],
  context: CanvasRenderingContext2D,
  // false for arrowhead shapes drawn after a draw window completed — they
  // render whole, not windowed (colors/dash flow still apply)
  applyDrawWindow = true,
  // true = this call fully drew the element itself (bypassing rough); the
  // caller must stop iterating any remaining shapes (e.g. arrowheads)
): boolean => {
  const activeStroke = getAnimationStroke(element.id);
  const stroke =
    applyDrawWindow || !activeStroke
      ? activeStroke
      : {
          dashOffset: activeStroke.dashOffset,
          dashLen: activeStroke.dashLen,
          dashGap: activeStroke.dashGap,
        };
  const colors = getAnimationColors(element.id);
  const isDark = theme === THEME.DARK;

  // background gradient: swap a CanvasGradient into the shared drawable's fill
  // for the duration of this draw, then restore the solid placeholder
  const shapeOptions = shape.options as {
    fill?: string | CanvasGradient;
    stroke?: string | CanvasGradient;
  };
  const gradientFill = element.backgroundGradient
    ? buildCanvasGradient(
        context,
        element.width,
        element.height,
        element.backgroundGradient,
        isDark,
      )
    : null;
  const prevGradientFill = shapeOptions.fill;
  const restoreGradientFill = () => {
    if (gradientFill) {
      shapeOptions.fill = prevGradientFill;
    }
  };
  if (gradientFill) {
    shapeOptions.fill = gradientFill;
  }

  // stroke gradient: same swap-and-restore hack, on options.stroke instead
  const gradientDelta = getAnimationGradientDelta(element.id);
  const effectiveStrokeGradient = element.strokeGradient
    ? applyGradientDelta(element.strokeGradient, gradientDelta)
    : null;
  const gradientStroke = effectiveStrokeGradient
    ? buildCanvasGradient(
        context,
        element.width,
        element.height,
        effectiveStrokeGradient,
        isDark,
      )
    : null;
  const prevGradientStroke = shapeOptions.stroke;
  const restoreGradientStroke = () => {
    if (gradientStroke) {
      shapeOptions.stroke = prevGradientStroke;
    }
  };
  if (gradientStroke) {
    shapeOptions.stroke = gradientStroke;
  }

  if (!stroke && !colors) {
    rc.draw(shape);
    restoreGradientFill();
    restoreGradientStroke();
    return false;
  }
  if (
    stroke?.drawProgress !== undefined &&
    isLinearElement(element) &&
    // a traveling window (drawSegment) always needs the exact path; draw-on /
    // erase only when rough split the path per segment, where the dash window
    // would restart at every corner
    (stroke.drawStart !== undefined || subpathCount(shape) > 2)
  ) {
    // drawSegment window on a polyline: canvas dashes restart at every
    // subpath, and rough splits a polyline into per-segment subpaths (each
    // segment × up to 2 roughness passes), so the offset dash window can't
    // travel across corners — instead stroke the raw points as ONE subpath
    // with the exact window (sketchy passes and arrowheads are omitted
    // while the segment animation is active); pen paths flatten their
    // bezier segments so the window travels the actual curve
    const pts = getFlattenedPathPoints(element);
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    }
    const start = Math.max(0, Math.min(1, stroke.drawStart ?? 0));
    const end = Math.max(start, Math.min(1, stroke.drawProgress));
    context.save();
    context.strokeStyle =
      gradientStroke ??
      applyDarkModeFilter(colors?.stroke ?? element.strokeColor, isDark);
    context.lineWidth = element.strokeWidth;
    context.lineCap = "round";
    context.lineJoin = "round";
    // gap = total + 1 guarantees a single visible window per subpath; one
    // subpath makes the dash offset exact along the whole polyline
    context.setLineDash([Math.max(total * (end - start), 0.0001), total + 1]);
    context.lineDashOffset = -total * start;
    context.beginPath();
    context.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) {
      context.lineTo(pts[i][0], pts[i][1]);
    }
    context.stroke();
    context.restore();
    restoreGradientFill();
    restoreGradientStroke();
    return true;
  }
  if (
    stroke?.drawProgress !== undefined &&
    !isLinearElement(element) &&
    subpathCount(shape) > 2
  ) {
    // rough splits shape outlines into per-side/per-pass subpaths and canvas
    // dashes restart at every subpath, so the dash-trick window saturates a
    // fraction into the tween (a 16-subpath diamond completes at ~6%) —
    // instead replay the drawable's own ops as ONE continuous path (subpath
    // jumps become straight connectors, hidden under the sketchy overlap) so
    // the exact [start, end] window travels the whole outline; the fill still
    // comes from rough, with its stroke passes hidden
    const total = continuousOpsLength(shape);
    const start = Math.max(0, Math.min(1, stroke.drawStart ?? 0));
    const end = Math.max(start, Math.min(1, stroke.drawProgress));
    const prevStrokeOpt = shapeOptions.stroke;
    shapeOptions.stroke = "transparent";
    rc.draw(shape);
    shapeOptions.stroke = prevStrokeOpt;
    context.save();
    context.strokeStyle =
      gradientStroke ??
      applyDarkModeFilter(colors?.stroke ?? element.strokeColor, isDark);
    context.lineWidth = element.strokeWidth;
    context.lineCap = "round";
    context.lineJoin = "round";
    if (start > 0.0005 || end < 0.9995) {
      // continuousOpsLength samples beziers as polylines (a slight under-
      // estimate), so a "fully drawn" dash window would clip the outline's
      // tail — skip the dash entirely once the window covers everything
      context.setLineDash([Math.max(total * (end - start), 0.0001), total + 1]);
      context.lineDashOffset = -total * start;
    }
    traceContinuousPath(context, shape);
    context.stroke();
    context.restore();
    restoreGradientFill();
    restoreGradientStroke();
    return false;
  }
  const options = shape.options as {
    strokeLineDash?: number[];
    strokeLineDashOffset?: number;
    stroke?: string;
    fill?: string;
  };
  const prevDash = options.strokeLineDash;
  const prevOffset = options.strokeLineDashOffset;
  const prevStroke = options.stroke;
  const prevFill = options.fill;
  if (stroke?.drawProgress !== undefined) {
    // rough may stroke the path twice (multiStroke); a dash PERIOD equal to
    // one pass's length makes both overlapping passes reveal the same
    // segment, so the [start, end] window tracks progress over the tween
    const total = drawnPathLength(shape);
    const geometric = geometricStrokeLength(element);
    const passes = Math.max(1, Math.min(2, Math.round(total / geometric)));
    const period = total / passes;
    // only ≤2-subpath drawables (one continuous pass each, e.g. ellipses)
    // reach this dash trick — multi-subpath outlines take the continuous
    // replay above — so the traveling [start, end] window works here too
    const start = Math.max(0, Math.min(1, stroke.drawStart ?? 0));
    const end = Math.max(start, Math.min(1, stroke.drawProgress));
    const window = end - start;
    options.strokeLineDash = [
      Math.max(period * window, 0.0001),
      Math.max(period * (1 - window), 0.0001),
    ];
    options.strokeLineDashOffset = -period * start;
  } else if (stroke?.dashOffset !== undefined) {
    // dash solid strokes so the flow is visible; clip-set pattern wins over
    // the element's own dash
    options.strokeLineDash = stroke.dashLen
      ? [stroke.dashLen, stroke.dashGap || stroke.dashLen]
      : prevDash ?? [8, 8 + element.strokeWidth];
    options.strokeLineDashOffset = -stroke.dashOffset;
  }
  if (colors?.stroke) {
    options.stroke = applyDarkModeFilter(colors.stroke, isDark);
  }
  if (colors?.background) {
    options.fill = applyDarkModeFilter(colors.background, isDark);
  }
  rc.draw(shape);
  options.strokeLineDash = prevDash;
  options.strokeLineDashOffset = prevOffset;
  options.stroke = prevStroke;
  options.fill = prevFill;
  restoreGradientFill();
  restoreGradientStroke();
  return false;
};

/**
 * Shape-to-shape morph: draws an interpolated outline between the element's
 * own shape and the morph target, replacing the rough drawable while the
 * morph is active (progress > 0).
 */
const unflattenCache = new WeakMap<number[], [number, number][]>();
const unflattenOutline = (flat: number[]): [number, number][] => {
  let pts = unflattenCache.get(flat);
  if (!pts) {
    pts = Array.from({ length: flat.length >> 1 }, (_, i) => [
      flat[i * 2],
      flat[i * 2 + 1],
    ]);
    unflattenCache.set(flat, pts);
  }
  return pts;
};

const drawMorphedShape = (
  context: CanvasRenderingContext2D,
  element: NonDeletedExcalidrawElement,
  morph: { shape: number; progress: number; outline?: number[] },
  theme: StaticCanvasRenderConfig["theme"],
) => {
  const isDark = theme === THEME.DARK;
  const colors = getAnimationColors(element.id);
  const progress = Math.max(0, Math.min(1, morph.progress));
  const SAMPLES = 64;
  // path elements (line/arrow/freedraw) morph from their own drawn outline
  // and render in their points' frame (the points bbox, not 0..width/height)
  const pathPoints =
    isLinearElement(element) || isFreeDrawElement(element)
      ? element.points
      : null;
  const sourceOutline = pathPoints ? elementOutlinePolygon(element) : null;
  const sourceShape = morphShapeForElementType(element.type);
  let boxX = 0;
  let boxY = 0;
  let boxW = element.width;
  let boxH = element.height;
  if (pathPoints) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of pathPoints) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    boxX = minX;
    boxY = minY;
    boxW = maxX - minX || 1;
    boxH = maxY - minY || 1;
  }
  // morph-to-element target: a flattened normalized polygon wins over the
  // built-in shape id. The flat array is set statically once per compile, so
  // its identity is stable across frames — unflatten once, not per frame.
  const outline: [number, number][] | null =
    morph.outline && morph.outline.length >= 6
      ? unflattenOutline(morph.outline)
      : null;
  context.save();
  context.beginPath();
  for (let i = 0; i <= SAMPLES; i++) {
    const t = (i % SAMPLES) / SAMPLES;
    const [sx, sy] = sourceOutline
      ? sampleOutlinePoints(sourceOutline, t)
      : sampleMorphShape(sourceShape, t);
    const [tx, ty] = outline
      ? sampleOutlinePoints(outline, t)
      : sampleMorphShape(morph.shape, t);
    const x = boxX + (sx + (tx - sx) * progress) * boxW;
    const y = boxY + (sy + (ty - sy) * progress) * boxH;
    if (i === 0) {
      context.moveTo(x, y);
    } else {
      context.lineTo(x, y);
    }
  }
  context.closePath();
  const background = colors?.background ?? element.backgroundColor;
  if (background && background !== "transparent") {
    context.fillStyle = applyDarkModeFilter(background, isDark);
    context.fill();
  }
  context.strokeStyle = applyDarkModeFilter(
    colors?.stroke ?? element.strokeColor,
    isDark,
  );
  // freedraw's perfect-freehand look is ~2× strokeWidth; approximate it so
  // the morphed outline doesn't visibly thin out when the morph kicks in
  context.lineWidth =
    element.strokeWidth * (isFreeDrawElement(element) ? 2 : 1);
  context.lineJoin = "round";
  context.lineCap = "round";
  context.stroke();
  context.restore();
};

/**
 * Stroke animations on freedraw (pencil) elements. The pencil stroke is a
 * FILLED outline polygon, not a stroked path, so the dash tricks don't apply:
 * - draw-on/erase/segment (`drawProgress`/`drawStart`): fills the freehand
 *   outline regenerated from the [start, end] arc-length window of the
 *   centerline — the stroke literally redraws itself, with the element's own
 *   point style (pressure-sensitive or constant width)
 * - dash flow (`dashOffset`): marching dashes stroked along the centerline
 * Returns true when it fully drew the element (caller skips the cached path).
 */
const drawFreedrawStrokeAnimation = (
  context: CanvasRenderingContext2D,
  element: ExcalidrawFreeDrawElement,
  stroke: {
    dashOffset?: number;
    dashLen?: number;
    dashGap?: number;
    drawProgress?: number;
    drawStart?: number;
  },
  theme: StaticCanvasRenderConfig["theme"],
): boolean => {
  if (element.points.length < 2) {
    return false;
  }
  const color = applyDarkModeFilter(
    getAnimationColors(element.id)?.stroke ?? element.strokeColor,
    theme === THEME.DARK,
  );
  if (stroke.drawProgress !== undefined) {
    const start = Math.max(0, Math.min(1, stroke.drawStart ?? 0));
    const end = Math.max(start, Math.min(1, stroke.drawProgress));
    const outline = getPartialFreedrawOutlinePoints(element, start, end);
    if (outline && outline.length >= 2) {
      context.save();
      context.fillStyle = color;
      context.beginPath();
      context.moveTo(outline[0][0], outline[0][1]);
      for (let i = 1; i < outline.length; i++) {
        context.lineTo(outline[i][0], outline[i][1]);
      }
      context.closePath();
      context.fill();
      context.restore();
    }
    // an empty window (progress 0) still owns the draw — render nothing
    return true;
  }
  if (stroke.dashOffset !== undefined) {
    const pts = element.points;
    context.save();
    context.strokeStyle = color;
    // approximate the freehand stroke's visual width
    context.lineWidth = element.strokeWidth * 2;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.setLineDash(
      stroke.dashLen
        ? [stroke.dashLen, stroke.dashGap || stroke.dashLen]
        : [8, 8 + element.strokeWidth],
    );
    context.lineDashOffset = -stroke.dashOffset;
    context.beginPath();
    context.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) {
      context.lineTo(pts[i][0], pts[i][1]);
    }
    context.stroke();
    context.restore();
    return true;
  }
  return false;
};

/**
 * ByteByteGo-style flow particles: glowing dots traveling along a linear
 * element's path, positions derived deterministically from the tweened
 * `flowPhase`.
 */
const drawFlowParticles = (
  context: CanvasRenderingContext2D,
  element: NonDeletedExcalidrawElement,
  flow: { phase: number; count: number; size: number; color?: string },
  theme: StaticCanvasRenderConfig["theme"],
) => {
  if (!isLinearElement(element) && !isFreeDrawElement(element)) {
    return;
  }
  // pen paths flatten their bezier segments so dots travel the actual curve
  const points = getFlattenedPathPoints(element);
  if (points.length < 2) {
    return;
  }
  // cumulative arc lengths for even spacing along the path
  const lengths: number[] = [0];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(
      points[i][0] - points[i - 1][0],
      points[i][1] - points[i - 1][1],
    );
    lengths.push(total);
  }
  if (total === 0) {
    return;
  }
  const pointAt = (t: number): [number, number] => {
    const target = t * total;
    for (let i = 1; i < lengths.length; i++) {
      if (target <= lengths[i]) {
        const segment = lengths[i] - lengths[i - 1];
        const f = segment === 0 ? 0 : (target - lengths[i - 1]) / segment;
        return [
          points[i - 1][0] + (points[i][0] - points[i - 1][0]) * f,
          points[i - 1][1] + (points[i][1] - points[i - 1][1]) * f,
        ];
      }
    }
    return [points[points.length - 1][0], points[points.length - 1][1]];
  };
  const color = applyDarkModeFilter(
    flow.color ?? getAnimationColors(element.id)?.stroke ?? element.strokeColor,
    theme === THEME.DARK,
  );
  const radius = Math.max(2, element.strokeWidth * flow.size);
  context.save();
  context.fillStyle = color;
  context.shadowColor = color;
  context.shadowBlur = radius * 2;
  for (let i = 0; i < flow.count; i++) {
    const t = (((flow.phase + i / flow.count) % 1) + 1) % 1;
    const [x, y] = pointAt(t);
    // ease dots in/out near the path ends so they don't pop
    const edge = Math.min(1, Math.min(t, 1 - t) * 12);
    context.globalAlpha = context.globalAlpha * Math.max(0, edge);
    context.beginPath();
    context.arc(x, y, radius, 0, 2 * Math.PI);
    context.fill();
    context.globalAlpha = 1;
  }
  context.restore();
};

/**
 * Draws `element` into `context` (already translated/rotated into the
 * element's own frame) and punches its pixel-eraser masks out of it.
 *
 * Brush strokes are excluded: renderBrushStroke already composites its own
 * masks inside its private offscreen canvas. Everything else gets the
 * detour here — a shape/text/image draws straight onto a canvas shared with
 * the rest of the scene, where a destination-out pass would eat the
 * elements underneath, so the draw is redirected to a private canvas first
 * and blitted back in one drawImage.
 *
 * The private canvas is allocated at the destination's *device* resolution
 * (read off the live transform) so masked elements stay exactly as crisp as
 * unmasked ones at any zoom or export scale.
 */
const drawElementOnCanvas = (
  element: NonDeletedExcalidrawElement,
  rc: RoughCanvas,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
) => {
  const masks = element.eraserMasks;
  if (
    !masks?.length ||
    (element.type === "freedraw" && isBrushElement(element)) ||
    typeof document === "undefined"
  ) {
    drawElementOnCanvasRaw(element, rc, context, renderConfig);
    return;
  }

  const pad = getCanvasPadding(element);
  // local-space box to cover: the element plus everywhere a mask stamp can
  // reach (masks may extend past the element — a stroke that runs off the
  // edge — and the crop must not clip the element itself either)
  let minX = -pad;
  let minY = -pad;
  let maxX = element.width + pad;
  let maxY = element.height + pad;
  // point-based elements (freedraw/line/arrow) can have local points left of
  // or above their own origin, so element.width/height alone under-covers them
  if ("points" in element) {
    for (const [px, py] of element.points) {
      minX = Math.min(minX, px - pad);
      minY = Math.min(minY, py - pad);
      maxX = Math.max(maxX, px + pad);
      maxY = Math.max(maxY, py + pad);
    }
  }
  for (const mask of masks) {
    for (const [px, py] of mask.points) {
      minX = Math.min(minX, px - mask.size);
      minY = Math.min(minY, py - mask.size);
      maxX = Math.max(maxX, px + mask.size);
      maxY = Math.max(maxY, py + mask.size);
    }
  }

  const t = context.getTransform();
  // uniform-scale magnitude of the current transform's axes; hypot (not
  // t.a/t.d) so a rotated context still reports the true pixel density
  const scaleX = Math.hypot(t.a, t.b) || 1;
  const scaleY = Math.hypot(t.c, t.d) || 1;
  const w = Math.min(8192, Math.ceil((maxX - minX) * scaleX));
  const h = Math.min(8192, Math.ceil((maxY - minY) * scaleY));
  if (!(w > 0) || !(h > 0)) {
    drawElementOnCanvasRaw(element, rc, context, renderConfig);
    return;
  }

  const off = document.createElement("canvas");
  off.width = w;
  off.height = h;
  const octx = off.getContext("2d");
  if (!octx) {
    drawElementOnCanvasRaw(element, rc, context, renderConfig);
    return;
  }
  octx.setTransform(scaleX, 0, 0, scaleY, -minX * scaleX, -minY * scaleY);
  drawElementOnCanvasRaw(element, rough.canvas(off), octx, renderConfig);
  applyEraserMasks(octx, masks, hashString(element.id));
  octx.setTransform(1, 0, 0, 1, 0, 0);

  // blit under the destination's own globalAlpha (element opacity, erasure
  // fade): the offscreen was drawn opaque so the mask could cut it cleanly,
  // and the alpha applies once, here, exactly as it would have on a direct
  // draw
  context.drawImage(off, minX, minY, maxX - minX, maxY - minY);
};

const drawElementOnCanvasRaw = (
  element: NonDeletedExcalidrawElement,
  rc: RoughCanvas,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
) => {
  switch (element.type) {
    case "rectangle":
    case "iframe":
    case "embeddable":
    case "diamond":
    case "ellipse": {
      context.lineJoin = "round";
      context.lineCap = "round";

      const morph = getAnimationMorph(element.id);
      if (morph && morph.progress > 0.001) {
        drawMorphedShape(context, element, morph, renderConfig.theme);
      } else {
        drawShapeWithStrokeAnimation(
          rc,
          animatedElementShape(element, renderConfig),
          element,
          renderConfig.theme,
          context,
        );
      }
      break;
    }
    case "arrow":
    case "line": {
      context.lineJoin = "round";
      context.lineCap = "round";

      const morph = getAnimationMorph(element.id);
      if (morph && morph.progress > 0.001) {
        // outline morph replaces the whole rendering (incl. arrowheads)
        drawMorphedShape(context, element, morph, renderConfig.theme);
      } else {
        // .some() stops as soon as a call reports it fully drew the element
        // itself (the exact-window bypass), skipping any remaining arrowhead
        // shapes — those live at the tip, so bring them back (whole, not
        // windowed) once the draw window has reached the end of the path
        const shapes = animatedElementShape(element, renderConfig);
        const bypassed = shapes.some((shape) =>
          drawShapeWithStrokeAnimation(
            rc,
            shape,
            element,
            renderConfig.theme,
            context,
          ),
        );
        if (
          bypassed &&
          (getAnimationStroke(element.id)?.drawProgress ?? 0) >= 0.999
        ) {
          for (let i = 1; i < shapes.length; i++) {
            drawShapeWithStrokeAnimation(
              rc,
              shapes[i],
              element,
              renderConfig.theme,
              context,
              false,
            );
          }
        }
      }
      const flow = getAnimationFlow(element.id);
      if (flow) {
        drawFlowParticles(context, element, flow, renderConfig.theme);
      }
      break;
    }
    case "freedraw": {
      // Draw directly to canvas
      context.save();

      const morph = getAnimationMorph(element.id);
      const strokeAnim = getAnimationStroke(element.id);
      if (morph && morph.progress > 0.001) {
        // outline morph replaces the pencil stroke entirely
        drawMorphedShape(context, element, morph, renderConfig.theme);
      } else if (isBrushElement(element)) {
        const color = applyDarkModeFilter(
          getAnimationColors(element.id)?.stroke ?? element.strokeColor,
          renderConfig.theme === THEME.DARK,
        );
        // draw-on animation maps to a stamp progress window
        const progress =
          strokeAnim && strokeAnim.drawProgress !== undefined
            ? {
                start: strokeAnim.drawStart ?? 0,
                end: strokeAnim.drawProgress,
              }
            : undefined;
        renderBrushStroke(context, element, color, progress);
      } else if (
        strokeAnim &&
        drawFreedrawStrokeAnimation(
          context,
          element,
          strokeAnim,
          renderConfig.theme,
        )
      ) {
        // draw-on window / dash flow fully drew the stroke (the loop-fill of
        // a closed path pops in when the animation finishes — intentional)
      } else {
        const shapes = animatedElementShape(element, renderConfig);

        for (const shape of shapes) {
          if (typeof shape === "string") {
            // the pencil stroke is a FILLED outline, so a stroke gradient
            // paints that fill; an animated scalar stroke color wins over it
            // (same precedence as drawShapeWithStrokeAnimation)
            const isDark = renderConfig.theme === THEME.DARK;
            const strokeAnimColor = getAnimationColors(element.id)?.stroke;
            const effectiveGradient =
              !strokeAnimColor && element.strokeGradient
                ? applyGradientDelta(
                    element.strokeGradient,
                    getAnimationGradientDelta(element.id),
                  )
                : null;
            context.fillStyle = effectiveGradient
              ? buildCanvasGradient(
                  context,
                  element.width,
                  element.height,
                  effectiveGradient,
                  isDark,
                )
              : applyDarkModeFilter(
                  strokeAnimColor ?? element.strokeColor,
                  isDark,
                );
            context.fill(new Path2D(shape));
          } else {
            drawShapeWithStrokeAnimation(
              rc,
              shape,
              element,
              renderConfig.theme,
              context,
            );
          }
        }
      }

      const freedrawFlow = getAnimationFlow(element.id);
      if (freedrawFlow) {
        drawFlowParticles(context, element, freedrawFlow, renderConfig.theme);
      }

      context.restore();
      break;
    }
    case "image": {
      context.save();
      const cacheEntry =
        element.fileId !== null
          ? renderConfig.imageCache.get(element.fileId)
          : null;
      const img = isInitializedImageElement(element)
        ? cacheEntry?.image
        : undefined;

      if (img != null && !(img instanceof Promise)) {
        if (element.roundness && context.roundRect) {
          context.beginPath();
          context.roundRect(
            0,
            0,
            element.width,
            element.height,
            getCornerRadii(Math.min(element.width, element.height), element),
          );
          context.clip();
        }

        const { x, y, width, height } = element.crop
          ? element.crop
          : {
              x: 0,
              y: 0,
              width: img.naturalWidth,
              height: img.naturalHeight,
            };

        const shouldInvertImage =
          renderConfig.theme === THEME.DARK &&
          cacheEntry?.mimeType === MIME_TYPES.svg;

        if (shouldInvertImage && isSafari) {
          const devicePixelRatio = window.devicePixelRatio || 1;
          const tempCanvas = document.createElement("canvas");
          tempCanvas.width = element.width * devicePixelRatio;
          tempCanvas.height = element.height * devicePixelRatio;
          const tempContext = tempCanvas.getContext("2d");

          if (tempContext) {
            tempContext.scale(devicePixelRatio, devicePixelRatio);
            tempContext.drawImage(
              img,
              x,
              y,
              width,
              height,
              0,
              0,
              element.width,
              element.height,
            );

            const imageData = tempContext.getImageData(
              0,
              0,
              tempCanvas.width,
              tempCanvas.height,
            );

            const data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
              data[i] = 255 - data[i];
              data[i + 1] = 255 - data[i + 1];
              data[i + 2] = 255 - data[i + 2];
            }

            tempContext.putImageData(imageData, 0, 0);
            context.drawImage(
              tempCanvas,
              0,
              0,
              tempCanvas.width,
              tempCanvas.height,
              0,
              0,
              element.width,
              element.height,
            );
          }
        } else {
          if (shouldInvertImage) {
            context.filter = DARK_THEME_FILTER;
          }

          context.drawImage(
            img,
            x,
            y,
            width,
            height,
            0 /* hardcoded for the selection box*/,
            0,
            element.width,
            element.height,
          );
        }
      } else {
        drawImagePlaceholder(element, context, renderConfig.theme);
      }
      context.restore();
      break;
    }
    default: {
      if (isTextElement(element)) {
        const rtl = isRTL(element.text);
        const shouldTemporarilyAttach = rtl && !context.canvas.isConnected;
        if (shouldTemporarilyAttach) {
          // to correctly render RTL text mixed with LTR, we have to append it
          // to the DOM
          document.body.appendChild(context.canvas);
        }
        context.canvas.setAttribute("dir", rtl ? "rtl" : "ltr");
        context.save();
        context.font = getFontString(element);
        // glyphs are filled: a stroke gradient paints them; an animated
        // scalar stroke color wins over it (same precedence as shapes)
        {
          const isDark = renderConfig.theme === THEME.DARK;
          const strokeAnimColor = getAnimationColors(element.id)?.stroke;
          const effectiveGradient =
            !strokeAnimColor && element.strokeGradient
              ? applyGradientDelta(
                  element.strokeGradient,
                  getAnimationGradientDelta(element.id),
                )
              : null;
          context.fillStyle = effectiveGradient
            ? buildCanvasGradient(
                context,
                element.width,
                element.height,
                effectiveGradient,
                isDark,
              )
            : applyDarkModeFilter(
                strokeAnimColor ?? element.strokeColor,
                isDark,
              );
        }
        context.textAlign = element.textAlign as CanvasTextAlign;

        // Canvas does not support multiline text by default
        // counter/odometer: the displayed string is the interpolated number
        const counter = getAnimationCounter(element.id);
        const lines = counter
          ? [formatCounterValue(counter)]
          : element.text.replace(/\r\n?/g, "\n").split("\n");

        const horizontalOffset =
          element.textAlign === "center"
            ? element.width / 2
            : element.textAlign === "right"
            ? element.width
            : 0;

        const lineHeightPx = getLineHeightInPx(
          element.fontSize,
          element.lineHeight,
        );

        const verticalOffset = getVerticalOffset(
          element.fontFamily,
          element.fontSize,
          lineHeightPx,
        );

        const textPath = getAnimationTextPath(element.id);
        if (textPath) {
          // Text on Path: glyphs ride the baked outline, normalized to the
          // element's width and centered on it — the text bends along the
          // path's SHAPE at the text's own location.
          // ponytail: paths much taller than the element clip at the canvas
          // padding; render at the padded-canvas level if that ever bites.
          const displayText = element.text.replace(/\s*\n\s*/g, " ");
          context.textAlign = "left";
          context.textBaseline = "middle";
          const chars = Array.from(displayText);
          const advances = chars.map((char) => context.measureText(char).width);
          const raw = textPath.outline;
          let minX = Infinity;
          let maxX = -Infinity;
          let minY = Infinity;
          let maxY = -Infinity;
          for (let i = 0; i < raw.length; i += 2) {
            minX = Math.min(minX, raw[i]);
            maxX = Math.max(maxX, raw[i]);
            minY = Math.min(minY, raw[i + 1]);
            maxY = Math.max(maxY, raw[i + 1]);
          }
          const fit = element.width / Math.max(1, maxX - minX);
          const scaled: number[] = [];
          for (let i = 0; i < raw.length; i += 2) {
            scaled.push(
              (raw[i] - (minX + maxX) / 2) * fit + element.width / 2,
              (raw[i + 1] - (minY + maxY) / 2) * fit + element.height / 2,
            );
          }
          const glyphs = layoutTextOnPath(advances, scaled, textPath.progress);
          if (glyphs) {
            for (let ci = 0; ci < chars.length; ci++) {
              if (!chars[ci].trim()) {
                continue;
              }
              const glyph = glyphs[ci];
              context.save();
              context.translate(glyph.x, glyph.y);
              context.rotate(glyph.angle);
              context.fillText(chars[ci], -advances[ci] / 2, 0);
              context.restore();
            }
            context.restore();
            if (shouldTemporarilyAttach) {
              context.canvas.remove();
            }
            break;
          }
          // degenerate outline → fall through to the normal paths below
        }

        const textEffect = getAnimationTextEffect(element.id);
        if (textEffect) {
          // per-character rendering: each glyph gets its own transform/alpha
          // derived deterministically from the tweened textProgress
          context.textAlign = "left";
          const totalChars = element.text.length;
          const baseAlpha = context.globalAlpha;
          // word/line splits: chars share their unit's index so whole words/
          // lines animate as blocks; split=0 keeps the legacy per-char path
          const units = textEffect.split
            ? buildTextUnits(lines, textEffect.split)
            : null;
          let charIndex = 0;
          for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            let lineCharIndex = 0;
            const lineWidth = context.measureText(line).width;
            const startX =
              element.textAlign === "center"
                ? horizontalOffset - lineWidth / 2
                : element.textAlign === "right"
                ? horizontalOffset - lineWidth
                : 0;
            const baseline = index * lineHeightPx + verticalOffset;
            const centerY = index * lineHeightPx + lineHeightPx / 2;
            let x = startX;
            for (const char of line) {
              const width = context.measureText(char).width;
              const state = getTextCharState(
                textEffect,
                units ? units.perLine[index][lineCharIndex] : charIndex,
                units ? units.count : totalChars,
                element.fontSize,
              );
              lineCharIndex++;
              if (char.trim() && state.alpha > 0.001) {
                const centerX = x + width / 2;
                const jitter = element.roughText
                  ? getRoughTextJitter(
                      element.seed,
                      charIndex,
                      element.fontSize,
                      element.roughness,
                    )
                  : null;
                context.save();
                context.translate(
                  centerX + state.dx + (jitter?.dx ?? 0),
                  centerY + state.dy + (jitter?.dy ?? 0),
                );
                if (state.rotate || jitter?.rotate) {
                  context.rotate(state.rotate + (jitter?.rotate ?? 0));
                }
                context.scale(state.scaleX, state.scaleY);
                context.translate(-centerX, -centerY);
                context.globalAlpha = baseAlpha * Math.min(1, state.alpha);
                context.fillText(state.char ?? char, x, baseline);
                context.restore();
              }
              x += width;
              charIndex++;
            }
            charIndex++; // count the newline so stagger flows across lines
          }
        } else if (element.roughText) {
          // rough handwriting: per-glyph wobble around the same advances the
          // normal path uses, so only the look changes, never the layout
          context.textAlign = "left";
          let charIndex = 0;
          for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            const lineWidth = context.measureText(line).width;
            let x =
              element.textAlign === "center"
                ? horizontalOffset - lineWidth / 2
                : element.textAlign === "right"
                ? horizontalOffset - lineWidth
                : 0;
            const baseline = index * lineHeightPx + verticalOffset;
            for (const char of line) {
              const width = context.measureText(char).width;
              if (char.trim()) {
                const jitter = getRoughTextJitter(
                  element.seed,
                  charIndex,
                  element.fontSize,
                  element.roughness,
                );
                context.save();
                context.translate(x + jitter.dx, baseline + jitter.dy);
                context.rotate(jitter.rotate);
                context.fillText(char, 0, 0);
                context.restore();
              }
              x += width;
              charIndex++;
            }
            charIndex++; // count the newline, same as the text-effect path
          }
        } else {
          for (let index = 0; index < lines.length; index++) {
            context.fillText(
              lines[index],
              horizontalOffset,
              index * lineHeightPx + verticalOffset,
            );
          }
        }
        context.restore();
        if (shouldTemporarilyAttach) {
          context.canvas.remove();
        }
      } else {
        throw new Error(`Unimplemented type ${element.type}`);
      }
    }
  }
};

export const elementWithCanvasCache = new WeakMap<
  ExcalidrawElement,
  ExcalidrawElementWithCanvas
>();

const generateElementWithCanvas = (
  element: NonDeletedExcalidrawElement,
  elementsMap: NonDeletedSceneElementsMap,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState | InteractiveCanvasAppState,
) => {
  const zoom: Zoom = renderConfig
    ? appState.zoom
    : {
        value: 1 as NormalizedZoomValue,
      };
  const prevElementWithCanvas = elementWithCanvasCache.get(element);
  const shouldRegenerateBecauseZoom =
    prevElementWithCanvas &&
    prevElementWithCanvas.zoomValue !== zoom.value &&
    !appState?.shouldCacheIgnoreZoom;
  const boundTextElement = getBoundTextElement(element, elementsMap);
  const boundTextElementVersion = boundTextElement?.version || null;
  const imageCrop = isImageElement(element) ? element.crop : null;

  const containingFrameOpacity =
    getContainingFrame(element, elementsMap)?.opacity || 100;

  const textAnimProgress =
    getAnimationOverride(element.id)?.textProgress ?? null;
  const strokeAnimState = getStrokeAnimState(element.id);

  if (
    !prevElementWithCanvas ||
    shouldRegenerateBecauseZoom ||
    prevElementWithCanvas.theme !== appState.theme ||
    prevElementWithCanvas.boundTextElementVersion !== boundTextElementVersion ||
    prevElementWithCanvas.imageCrop !== imageCrop ||
    prevElementWithCanvas.containingFrameOpacity !== containingFrameOpacity ||
    prevElementWithCanvas.textAnimProgress !== textAnimProgress ||
    prevElementWithCanvas.strokeAnimState !== strokeAnimState ||
    // since we rotate the canvas when copying from cached canvas, we don't
    // regenerate the cached canvas. But we need to in case of labels which are
    // cached alongside the arrow, and we want the labels to remain unrotated
    // with respect to the arrow.
    (isArrowElement(element) &&
      boundTextElement &&
      element.angle !== prevElementWithCanvas.angle)
  ) {
    const elementWithCanvas = generateElementCanvas(
      element,
      elementsMap,
      zoom,
      renderConfig,
      appState,
    );

    if (!elementWithCanvas) {
      return null;
    }

    elementWithCanvasCache.set(element, elementWithCanvas);

    return elementWithCanvas;
  }
  return prevElementWithCanvas;
};

/** Explode/Assemble: slices the element's cached canvas into an n×n grid
 * and draws every cell on its seeded trajectory. Runs inside the caller's
 * placement transform, dest rect args match the intact drawImage call. */
const drawFragmentedImage = (
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
  frag: FragmentConfig,
  seed: number,
) => {
  const { cells, n } = getFragmentCells(frag, seed, destW, destH);
  const srcW = canvas.width / n;
  const srcH = canvas.height / n;
  const cellW = destW / n;
  const cellH = destH / n;
  const baseAlpha = context.globalAlpha;
  for (const cell of cells) {
    if (cell.alpha <= 0.01) {
      continue;
    }
    const cx = destX + (cell.i + 0.5) * cellW + cell.dx;
    const cy = destY + (cell.j + 0.5) * cellH + cell.dy;
    context.save();
    context.globalAlpha = baseAlpha * cell.alpha;
    context.translate(cx, cy);
    if (cell.rotate) {
      context.rotate(cell.rotate);
    }
    if (cell.scale !== 1) {
      context.scale(cell.scale, cell.scale);
    }
    if (cell.tri !== undefined) {
      // shard mode: clip this half-cell to its triangle (corners in local
      // coords; diag 0 cuts TL→BR, diag 1 cuts TR→BL)
      const w2 = cellW / 2;
      const h2 = cellH / 2;
      const corners =
        cell.diag === 1
          ? cell.tri === 0
            ? [
                [-w2, -h2],
                [w2, -h2],
                [-w2, h2],
              ]
            : [
                [w2, -h2],
                [w2, h2],
                [-w2, h2],
              ]
          : cell.tri === 0
          ? [
              [-w2, -h2],
              [w2, -h2],
              [w2, h2],
            ]
          : [
              [-w2, -h2],
              [w2, h2],
              [-w2, h2],
            ];
      context.beginPath();
      context.moveTo(corners[0][0], corners[0][1]);
      context.lineTo(corners[1][0], corners[1][1]);
      context.lineTo(corners[2][0], corners[2][1]);
      context.closePath();
      context.clip();
    }
    context.drawImage(
      canvas,
      cell.i * srcW,
      cell.j * srcH,
      srcW,
      srcH,
      -cellW / 2,
      -cellH / 2,
      cellW,
      cellH,
    );
    context.restore();
  }
  context.globalAlpha = baseAlpha;
};

/** Confetti / sparkle particles over the element's dest rect — drawn at the
 * scene-compositing level so they fly beyond the bbox unclipped. */
const drawEmitterParticles = (
  context: CanvasRenderingContext2D,
  emit: EmitterConfig,
  seed: number,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
) => {
  const particles = getEmitterParticles(emit, seed, destW, destH);
  const baseAlpha = context.globalAlpha;
  for (const particle of particles) {
    if (particle.alpha <= 0.01) {
      continue;
    }
    context.save();
    context.globalAlpha = baseAlpha * particle.alpha;
    context.translate(destX + particle.x, destY + particle.y);
    context.rotate(particle.rotate);
    if (emit.kind === EMIT_KINDS.sparkle) {
      // warm-white diamond
      context.fillStyle = "#ffe9a8";
      context.beginPath();
      context.moveTo(0, -particle.size);
      context.lineTo(particle.size * 0.35, 0);
      context.lineTo(0, particle.size);
      context.lineTo(-particle.size * 0.35, 0);
      context.closePath();
      context.fill();
    } else {
      context.fillStyle = CONFETTI_COLORS[particle.colorIndex];
      context.fillRect(
        -particle.size / 2,
        -particle.size / 3,
        particle.size,
        (particle.size * 2) / 3,
      );
    }
    context.restore();
  }
  context.globalAlpha = baseAlpha;
};

const drawElementFromCanvas = (
  elementWithCanvas: ExcalidrawElementWithCanvas,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState | InteractiveCanvasAppState,
  allElementsMap: NonDeletedSceneElementsMap,
) => {
  const element = elementWithCanvas.element;
  const padding = getCanvasPadding(element);
  const zoom = elementWithCanvas.scale;
  const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, allElementsMap);
  const cx = ((x1 + x2) / 2 + appState.scrollX) * window.devicePixelRatio;
  const cy = ((y1 + y2) / 2 + appState.scrollY) * window.devicePixelRatio;

  context.save();
  context.scale(1 / window.devicePixelRatio, 1 / window.devicePixelRatio);

  const boundTextElement = getBoundTextElement(element, allElementsMap);

  if (isArrowElement(element) && boundTextElement) {
    const offsetX =
      (elementWithCanvas.boundTextCanvas.width -
        elementWithCanvas.canvas!.width) /
      2;
    const offsetY =
      (elementWithCanvas.boundTextCanvas.height -
        elementWithCanvas.canvas!.height) /
      2;
    context.translate(cx, cy);
    context.drawImage(
      elementWithCanvas.boundTextCanvas,
      (-(x2 - x1) / 2) * window.devicePixelRatio - offsetX / zoom - padding,
      (-(y2 - y1) / 2) * window.devicePixelRatio - offsetY / zoom - padding,
      elementWithCanvas.boundTextCanvas.width / zoom,
      elementWithCanvas.boundTextCanvas.height / zoom,
    );
  } else {
    // we translate context to element center so that rotation and scale
    // originates from the element center
    context.translate(cx, cy);

    context.rotate(element.angle);

    if (
      "scale" in elementWithCanvas.element &&
      !isPendingImageElement(element, renderConfig)
    ) {
      context.scale(
        elementWithCanvas.element.scale[0],
        elementWithCanvas.element.scale[1],
      );
    }

    // revert afterwards we don't have account for it during drawing
    context.translate(-cx, -cy);

    const destX =
      (x1 + appState.scrollX) * window.devicePixelRatio -
      (padding * elementWithCanvas.scale) / elementWithCanvas.scale;
    const destY =
      (y1 + appState.scrollY) * window.devicePixelRatio -
      (padding * elementWithCanvas.scale) / elementWithCanvas.scale;
    const destW = elementWithCanvas.canvas!.width / elementWithCanvas.scale;
    const destH = elementWithCanvas.canvas!.height / elementWithCanvas.scale;
    const frag = getAnimationFragment(element.id);
    if (frag) {
      // explode/assemble: the same canvas, sliced and scattered
      drawFragmentedImage(
        context,
        elementWithCanvas.canvas!,
        destX,
        destY,
        destW,
        destH,
        frag,
        element.seed,
      );
    } else {
      context.drawImage(elementWithCanvas.canvas!, destX, destY, destW, destH);
    }

    const emit = getAnimationEmitter(element.id);
    if (emit) {
      drawEmitterParticles(
        context,
        emit,
        element.seed,
        destX,
        destY,
        destW,
        destH,
      );
    }

    if (
      import.meta.env.VITE_APP_DEBUG_ENABLE_TEXT_CONTAINER_BOUNDING_BOX ===
        "true" &&
      hasBoundTextElement(element)
    ) {
      const textElement = getBoundTextElement(
        element,
        allElementsMap,
      ) as ExcalidrawTextElementWithContainer;
      const coords = getContainerCoords(element);
      context.strokeStyle = "#c92a2a";
      context.lineWidth = 3;
      context.strokeRect(
        (coords.x + appState.scrollX) * window.devicePixelRatio,
        (coords.y + appState.scrollY) * window.devicePixelRatio,
        getBoundTextMaxWidth(element, textElement) * window.devicePixelRatio,
        getBoundTextMaxHeight(element, textElement) * window.devicePixelRatio,
      );
    }
  }
  context.restore();

  // Clear the nested element we appended to the DOM
};

export const renderSelectionElement = (
  element: NonDeletedExcalidrawElement,
  context: CanvasRenderingContext2D,
  appState: InteractiveCanvasAppState,
  selectionColor: InteractiveCanvasRenderConfig["selectionColor"],
) => {
  context.save();
  context.translate(element.x + appState.scrollX, element.y + appState.scrollY);
  context.fillStyle = "rgba(0, 0, 200, 0.04)";

  // render from 0.5px offset  to get 1px wide line
  // https://stackoverflow.com/questions/7530593/html5-canvas-and-line-width/7531540#7531540
  // TODO can be be improved by offseting to the negative when user selects
  // from right to left
  const offset = 0.5 / appState.zoom.value;

  context.fillRect(offset, offset, element.width, element.height);
  context.lineWidth = 1 / appState.zoom.value;
  context.strokeStyle = selectionColor;
  context.strokeRect(offset, offset, element.width, element.height);

  context.restore();
};

/**
 * Paints a blurred copy of whatever is already on `context` (z-order below
 * the element), clipped to the element's shape — must run right before the
 * element itself is drawn. Covers both canvas and PNG export (same renderer).
 */
/** How the outline path maps to the element's painted geometry: `fill` for
 *  closed regions (rect/polygon fills), `stroke` for open paths whose paint
 *  is the stroke band itself (pencil lines, open pen paths, arrows), `band`
 *  for freedraw silhouettes (filled outline, dilated up to the minimum band
 *  so thin strokes still show a visible frosted ribbon) — matching CSS
 *  backdrop-filter semantics: the blur belongs to the painted geometry. */
type OutlineClip = { path: Path2D; mode: "fill" | "stroke" | "band" };

/** Minimum frosted-band width (scene px) for stroke-painted elements —
 *  backdrop blur behind a hairline is imperceptible, so thin strokes get a
 *  visible glass ribbon following the line instead of nothing. */
const MIN_BLUR_BAND = 14;

const outlinePathCache = new WeakMap<
  ExcalidrawElement,
  { version: number; clip: OutlineClip | null }
>();

/** Builds a Path2D tracing the element's actual outline (freedraw stroke,
 *  pen bezier path, or straight-segment line/arrow) in the same local,
 *  points-relative coordinate space `element.points` live in. Returns null
 *  for element types without a points-based outline (caller falls back to
 *  a rect/roundRect clip). Cached per element version — geometry only
 *  changes when the element mutates. */
const buildElementOutlinePath = (
  element: NonDeletedExcalidrawElement,
  renderConfig: StaticCanvasRenderConfig,
): OutlineClip | null => {
  const cached = outlinePathCache.get(element);
  if (cached && cached.version === element.version) {
    return cached.clip;
  }
  const clip = ((): OutlineClip | null => {
    if (element.type === "freedraw") {
      // the perfect-freehand outline IS the stroke band — fill it (dilated
      // to MIN_BLUR_BAND in the mask pass so thin pencils stay visible)
      const shapes = ShapeCache.generateElementShape(element, renderConfig);
      const svgPath = shapes.find((shape) => typeof shape === "string");
      return svgPath ? { path: new Path2D(svgPath), mode: "band" } : null;
    }
    if (element.type === "line" || element.type === "arrow") {
      const closed =
        element.type === "line" && (element as ExcalidrawLineElement).polygon;
      if (isPenPath(element)) {
        const d = buildPenPathD(
          element.points,
          element.handles,
          element.polygon,
        );
        return d
          ? { path: new Path2D(d), mode: closed ? "fill" : "stroke" }
          : null;
      }
      if (element.points.length >= 2) {
        const p = new Path2D();
        element.points.forEach(([x, y], i) =>
          i === 0 ? p.moveTo(x, y) : p.lineTo(x, y),
        );
        if (closed) {
          p.closePath();
        }
        return { path: p, mode: closed ? "fill" : "stroke" };
      }
    }
    return null;
  })();
  outlinePathCache.set(element, { version: element.version, clip });
  return clip;
};

/** Reused scratch canvas for the per-frame background-blur pass — grows to
 *  the largest size needed and is never shrunk, avoiding a canvas allocation
 *  (and GC churn) on every rendered frame per blurred element. */
let blurScratchCanvas: HTMLCanvasElement | null = null;
const getBlurScratchCanvas = (
  width: number,
  height: number,
): HTMLCanvasElement => {
  if (!blurScratchCanvas) {
    blurScratchCanvas = document.createElement("canvas");
  }
  if (blurScratchCanvas.width < width) {
    blurScratchCanvas.width = width;
  }
  if (blurScratchCanvas.height < height) {
    blurScratchCanvas.height = height;
  }
  return blurScratchCanvas;
};

/** Second reused scratch: stroke-band mask for open paths (see OutlineClip). */
let blurMaskCanvas: HTMLCanvasElement | null = null;
const getBlurMaskCanvas = (
  width: number,
  height: number,
): HTMLCanvasElement => {
  if (!blurMaskCanvas) {
    blurMaskCanvas = document.createElement("canvas");
  }
  if (blurMaskCanvas.width < width) {
    blurMaskCanvas.width = width;
  }
  if (blurMaskCanvas.height < height) {
    blurMaskCanvas.height = height;
  }
  return blurMaskCanvas;
};

const renderBackgroundBlur = (
  element: NonDeletedExcalidrawElement,
  elementsMap: RenderableElementsMap,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState | InteractiveCanvasAppState,
) => {
  const bg = element.effects?.backgroundBlur;
  if (!bg?.enabled || bg.radius <= 0 || !isCanvasFilterSupported()) {
    return;
  }
  const srcCanvas = context.canvas;
  if (!srcCanvas || !srcCanvas.width || !srcCanvas.height) {
    return;
  }
  const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
  const cx = (x1 + x2) / 2 + appState.scrollX;
  const cy = (y1 + y2) / 2 + appState.scrollY;
  const halfW = (x2 - x1) / 2;
  const halfH = (y2 - y1) / 2;

  // device-space AABB of the rotated element
  const transform = context.getTransform();
  const deviceScale = Math.hypot(transform.a, transform.b) || 1;
  const cos = Math.cos(element.angle);
  const sin = Math.sin(element.angle);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [dx, dy] of [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ]) {
    const rx = cx + dx * cos - dy * sin;
    const ry = cy + dx * sin + dy * cos;
    const p = new DOMPoint(rx, ry).matrixTransform(transform);
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  const radiusPx = bg.radius * deviceScale;
  // pad the sample so edge pixels don't pull in transparent
  const pad = Math.ceil(radiusPx * 2);
  const sx = Math.max(0, Math.floor(minX) - pad);
  const sy = Math.max(0, Math.floor(minY) - pad);
  const sw = Math.min(srcCanvas.width, Math.ceil(maxX) + pad) - sx;
  const sh = Math.min(srcCanvas.height, Math.ceil(maxY) + pad) - sy;
  if (sw <= 0 || sh <= 0) {
    return;
  }

  // blur bleeds up to ~2×radius beyond the drawn image — keep that margin
  // inside the scratch so the 9-arg blit below reads fully valid pixels
  const bleed = pad;
  const tmp = getBlurScratchCanvas(sw + bleed, sh + bleed);
  const tctx = tmp.getContext("2d");
  if (!tctx) {
    return;
  }
  tctx.filter = "none";
  tctx.clearRect(0, 0, sw + bleed, sh + bleed);
  tctx.filter = `blur(${radiusPx}px)`;
  tctx.drawImage(srcCanvas, sx, sy, sw, sh, 0, 0, sw, sh);
  tctx.filter = "none";

  const outlineClip = buildElementOutlinePath(element, renderConfig);

  if (outlineClip?.mode === "stroke" || outlineClip?.mode === "band") {
    // stroke-painted geometry — mask the blurred patch (device space) by the
    // stroke band instead of clipping to the path's enclosed region.
    const shiftX = (x1 + x2) / 2 - element.x;
    const shiftY = (y1 + y2) / 2 - element.y;
    const mask = getBlurMaskCanvas(sw, sh);
    const mctx = mask.getContext("2d");
    if (!mctx) {
      return;
    }
    mctx.setTransform(1, 0, 0, 1, 0, 0);
    mctx.clearRect(0, 0, sw, sh);
    // reproduce the element's device-space transform, shifted by the patch
    // origin so the mask lines up with the sampled region
    mctx.setTransform(1, 0, 0, 1, -sx, -sy);
    mctx.transform(
      transform.a,
      transform.b,
      transform.c,
      transform.d,
      transform.e,
      transform.f,
    );
    mctx.translate(cx, cy);
    mctx.rotate(element.angle);
    mctx.translate(-shiftX, -shiftY);
    mctx.lineCap = "round";
    mctx.lineJoin = "round";
    mctx.strokeStyle = "#fff";
    mctx.fillStyle = "#fff";
    if (outlineClip.mode === "band") {
      // freedraw silhouette: fill it, then dilate up to the minimum band so
      // hairline pencils still show a visible frosted ribbon
      mctx.fill(outlineClip.path);
      const dilation = Math.max(0, MIN_BLUR_BAND - element.strokeWidth);
      if (dilation > 0) {
        mctx.lineWidth = dilation;
        mctx.stroke(outlineClip.path);
      }
    } else {
      // open path: band = stroke width (+4 roughjs wobble headroom), never
      // thinner than the minimum visible band
      mctx.lineWidth = Math.max(element.strokeWidth + 4, MIN_BLUR_BAND);
      mctx.stroke(outlineClip.path);
    }

    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.globalCompositeOperation = "destination-in";
    tctx.drawImage(mask, 0, 0, sw, sh, 0, 0, sw, sh);
    tctx.globalCompositeOperation = "source-over";

    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.drawImage(tmp, 0, 0, sw, sh, sx, sy, sw, sh);
    context.restore();
    return;
  }

  context.save();
  // clip to the element's shape in its local (rotated) space
  context.translate(cx, cy);
  context.rotate(element.angle);
  const w = x2 - x1;
  const h = y2 - y1;
  if (outlineClip) {
    // element.points live relative to element.x/y — shift into the local
    // frame centered on the (unrotated) bbox, matching halfW/halfH above
    const shiftX = (x1 + x2) / 2 - element.x;
    const shiftY = (y1 + y2) / 2 - element.y;
    context.translate(-shiftX, -shiftY);
    context.clip(outlineClip.path);
  } else {
    context.beginPath();
    switch (element.type) {
      case "ellipse":
        context.ellipse(0, 0, halfW, halfH, 0, 0, Math.PI * 2);
        break;
      case "diamond":
        context.moveTo(0, -halfH);
        context.lineTo(halfW, 0);
        context.lineTo(0, halfH);
        context.lineTo(-halfW, 0);
        context.closePath();
        break;
      default: {
        // rounded/plain rect clip for everything else without a points-based
        // outline (rectangle, text, image, elbow arrow, frame)
        const radii = element.roundness
          ? getCornerRadii(Math.min(w, h), element)
          : null;
        if (radii?.some((r) => r > 0) && context.roundRect) {
          context.roundRect(-halfW, -halfH, w, h, radii);
        } else {
          context.rect(-halfW, -halfH, w, h);
        }
        break;
      }
    }
    context.clip();
  }
  // paint the blurred patch back in device space (9-arg blit: the scratch
  // canvas is shared and may be larger than the sampled region)
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.drawImage(tmp, 0, 0, sw, sh, sx, sy, sw, sh);
  context.restore();
};

/**
 * Paints a frame's surface (background fill + texture/noise) as a standalone
 * layer. staticScene calls this in a pre-pass BEFORE the main z-order loop so a
 * screen's white artboard always sits BEHIND its children: a shape drawn or
 * moved into a frame gets reordered just below the frame, and an opaque fill
 * painted at the frame's own z-index would otherwise cover it. The frame
 * OUTLINE still draws inline (on top) in renderElement's "frame" case.
 */
export const renderFrameSurfaceLayer = (
  element: ExcalidrawFrameLikeElement,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState | InteractiveCanvasAppState,
) => {
  if (!appState.frameRendering.enabled) {
    return;
  }
  const hasFill =
    !!element.backgroundColor && element.backgroundColor !== "transparent";
  if (!hasFill && !hasEnabledEffects(element)) {
    return;
  }
  context.save();
  context.translate(element.x + appState.scrollX, element.y + appState.scrollY);
  const scaleFactor = window.devicePixelRatio * appState.zoom.value;
  const surface = document.createElement("canvas");
  surface.width = Math.max(1, Math.round(element.width * scaleFactor));
  surface.height = Math.max(1, Math.round(element.height * scaleFactor));
  renderFrameSurface(surface, element, scaleFactor, renderConfig.imageCache);
  context.drawImage(surface, 0, 0, element.width, element.height);
  context.restore();
};

export const renderElement = (
  element: NonDeletedExcalidrawElement,
  elementsMap: RenderableElementsMap,
  allElementsMap: NonDeletedSceneElementsMap,
  rc: RoughCanvas,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState | InteractiveCanvasAppState,
) => {
  renderBackgroundBlur(element, elementsMap, context, renderConfig, appState);

  const reduceAlphaForSelection =
    appState.openDialog?.name === "elementLinkSelector" &&
    !appState.selectedElementIds[element.id] &&
    !appState.hoveredElementIds[element.id];

  context.globalAlpha = getRenderOpacity(
    element,
    getContainingFrame(element, elementsMap),
    renderConfig.elementsPendingErasure,
    renderConfig.pendingFlowchartNodes,
    reduceAlphaForSelection
      ? DEFAULT_REDUCED_GLOBAL_ALPHA
      : getAnimationOpacity(element),
  );

  switch (element.type) {
    case "magicframe":
    case "frame": {
      if (appState.frameRendering.enabled) {
        context.save();
        context.translate(
          element.x + appState.scrollX,
          element.y + appState.scrollY,
        );

        // The surface (bg fill + texture + noise) is painted in a separate
        // background pass (renderFrameSurfaceLayer) BENEATH all elements, so it
        // never covers children sitting below the frame in z-order. Only the
        // outline is drawn inline here, on top.
        if (appState.frameRendering.outline) {
          context.lineWidth = FRAME_STYLE.strokeWidth / appState.zoom.value;
          context.strokeStyle = applyDarkModeFilter(
            FRAME_STYLE.strokeColor,
            appState.theme === THEME.DARK,
          );

          // TODO change later to only affect AI frames
          if (isMagicFrameElement(element)) {
            context.strokeStyle =
              appState.theme === THEME.LIGHT
                ? "#7affd7"
                : applyDarkModeFilter("#1d8264");
          }

          if (FRAME_STYLE.radius && context.roundRect) {
            context.beginPath();
            context.roundRect(
              0,
              0,
              element.width,
              element.height,
              FRAME_STYLE.radius / appState.zoom.value,
            );
            context.stroke();
            context.closePath();
          } else {
            context.strokeRect(0, 0, element.width, element.height);
          }
        }

        context.restore();
      }
      break;
    }
    case "freedraw": {
      // Effects (shadow/blur/noise/texture) are baked into the element canvas
      // by generateElementCanvas → applyEffectsToCanvas, which only the cached
      // (else) path runs. The direct-vector export path skips them, so elements
      // WITH effects must go through the cache path even during export.
      // Brush elements must also always go through the cache path: the
      // direct-vector branch below draws the plain perfect-freehand fill,
      // bypassing renderBrushStroke entirely.
      if (
        (renderConfig.isExporting ||
          isElementCanvasResolutionCapped(
            element,
            elementsMap,
            appState.zoom,
          )) &&
        !hasEnabledEffects(element) &&
        !isBrushElement(element) &&
        // fragments slice the cached canvas — export must use it too
        !getAnimationFragment(element.id)
      ) {
        const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
        const cx = (x1 + x2) / 2 + appState.scrollX;
        const cy = (y1 + y2) / 2 + appState.scrollY;
        const shiftX = (x2 - x1) / 2 - (element.x - x1);
        const shiftY = (y2 - y1) / 2 - (element.y - y1);
        context.save();
        context.translate(cx, cy);
        context.rotate(element.angle);
        context.translate(-shiftX, -shiftY);
        drawElementOnCanvas(element, rc, context, renderConfig);
        context.restore();
      } else {
        const elementWithCanvas = generateElementWithCanvas(
          element,
          allElementsMap,
          renderConfig,
          appState,
        );
        if (!elementWithCanvas) {
          return;
        }

        drawElementFromCanvas(
          elementWithCanvas,
          context,
          renderConfig,
          appState,
          allElementsMap,
        );
      }

      break;
    }
    case "rectangle":
    case "diamond":
    case "ellipse":
    case "line":
    case "arrow":
    case "image":
    case "text":
    case "iframe":
    case "embeddable": {
      // Elements WITH effects must use the cached path during export so their
      // shadow/blur/noise/texture (baked in by applyEffectsToCanvas) survive —
      // the direct-vector export path below skips effect baking. See freedraw.
      // Fragmenting elements likewise: the effect slices the cached canvas.
      if (
        (renderConfig.isExporting ||
          isElementCanvasResolutionCapped(
            element,
            elementsMap,
            appState.zoom,
          )) &&
        !hasEnabledEffects(element) &&
        !getAnimationFragment(element.id)
      ) {
        const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
        const centerX = (x1 + x2) / 2;
        const centerY = (y1 + y2) / 2;
        const cx = centerX + appState.scrollX;
        const cy = centerY + appState.scrollY;
        let shiftX = (x2 - x1) / 2 - (element.x - x1);
        let shiftY = (y2 - y1) / 2 - (element.y - y1);
        if (isTextElement(element)) {
          const container = getContainerElement(element, elementsMap);
          if (isArrowElement(container)) {
            const boundTextCoords =
              LinearElementEditor.getBoundTextElementPosition(
                container,
                element as ExcalidrawTextElementWithContainer,
                elementsMap,
              );
            shiftX = (x2 - x1) / 2 - (boundTextCoords.x - x1);
            shiftY = (y2 - y1) / 2 - (boundTextCoords.y - y1);
          }
        }
        context.save();
        context.translate(cx, cy);

        const boundTextElement = getBoundTextElement(element, elementsMap);

        if (isArrowElement(element) && boundTextElement) {
          // Draw arrow directly as vector and clear label hole separately.
          // This avoids temp-canvas bitmap blit which introduces resampling blur.
          shiftX = element.width / 2 - (element.x - x1);
          shiftY = element.height / 2 - (element.y - y1);

          context.save();
          context.rotate(element.angle);
          context.translate(-shiftX, -shiftY);
          drawElementOnCanvas(element, rc, context, renderConfig);
          context.restore();

          const [, , , , boundTextCx, boundTextCy] = getElementAbsoluteCoords(
            boundTextElement,
            elementsMap,
          );
          const holeX =
            boundTextCx -
            centerX -
            boundTextElement.width / 2 -
            BOUND_TEXT_PADDING;
          const holeY =
            boundTextCy -
            centerY -
            boundTextElement.height / 2 -
            BOUND_TEXT_PADDING;
          const holeWidth = boundTextElement.width + BOUND_TEXT_PADDING * 2;
          const holeHeight = boundTextElement.height + BOUND_TEXT_PADDING * 2;

          const isTransparentHole =
            "viewBackgroundColor" in appState &&
            (appState.viewBackgroundColor === "transparent" ||
              !appState.viewBackgroundColor);
          if (!isTransparentHole) {
            context.save();
            context.fillStyle = applyDarkModeFilter(
              renderConfig.canvasBackgroundColor,
              renderConfig.theme === THEME.DARK,
            );
            context.fillRect(holeX, holeY, holeWidth, holeHeight);
            context.restore();
          } else {
            context.clearRect(holeX, holeY, holeWidth, holeHeight);
          }
        } else {
          context.rotate(element.angle);

          if (element.type === "image") {
            // note: scale must be applied *after* rotating
            context.scale(element.scale[0], element.scale[1]);
          }

          context.translate(-shiftX, -shiftY);
          drawElementOnCanvas(element, rc, context, renderConfig);
        }

        context.restore();
        // not exporting → optimized rendering (cache & render from element
        // canvases)
      } else {
        const elementWithCanvas = generateElementWithCanvas(
          element,
          allElementsMap,
          renderConfig,
          appState,
        );

        if (!elementWithCanvas) {
          return;
        }

        const currentImageSmoothingStatus = context.imageSmoothingEnabled;

        if (
          // do not disable smoothing during zoom as blurry shapes look better
          // on low resolution (while still zooming in) than sharp ones
          !appState?.shouldCacheIgnoreZoom &&
          // cappedElementCanvasSize caps the cached bitmap's resolution once
          // element size × zoom crosses the safe canvas area/dimension limit
          // (16.7M px² / 32767px, see cappedElementCanvasSize). Past that
          // point elementWithCanvas.scale < zoom.value: the cache is lower
          // resolution than the destination, so nearest-neighbor upscaling
          // (smoothing off) produces hard stairstep pixelation. Blurring is
          // the lesser artifact, so keep smoothing on in that case.
          elementWithCanvas.scale >= appState.zoom.value &&
          // angle is 0 -> always disable smoothing
          (!element.angle ||
            // or check if angle is a right angle in which case we can still
            // disable smoothing without adversely affecting the result
            // We need less-than comparison because of FP artihmetic
            isRightAngleRads(element.angle))
        ) {
          // Disabling smoothing makes output much sharper, especially for
          // text. Unless for non-right angles, where the aliasing is really
          // terrible on Chromium.
          //
          // Note that `context.imageSmoothingQuality="high"` has almost
          // zero effect.
          //
          context.imageSmoothingEnabled = false;
        }

        if (
          element.id === appState.croppingElementId &&
          isImageElement(elementWithCanvas.element) &&
          elementWithCanvas.element.crop !== null
        ) {
          context.save();
          context.globalAlpha = 0.1;

          const uncroppedElementCanvas = generateElementCanvas(
            getUncroppedImageElement(elementWithCanvas.element, elementsMap),
            allElementsMap,
            appState.zoom,
            renderConfig,
            appState,
          );

          if (uncroppedElementCanvas) {
            drawElementFromCanvas(
              uncroppedElementCanvas,
              context,
              renderConfig,
              appState,
              allElementsMap,
            );
          }

          context.restore();
        }

        drawElementFromCanvas(
          elementWithCanvas,
          context,
          renderConfig,
          appState,
          allElementsMap,
        );

        // reset
        context.imageSmoothingEnabled = currentImageSmoothingStatus;
      }
      break;
    }
    default: {
      // @ts-ignore
      throw new Error(`Unimplemented type ${element.type}`);
    }
  }

  context.globalAlpha = 1;
};

export function getFreedrawOutlineAsSegments(
  element: ExcalidrawFreeDrawElement,
  points: [number, number][],
  elementsMap: ElementsMap,
) {
  const bounds = getElementBounds(
    {
      ...element,
      angle: 0 as Radians,
    },
    elementsMap,
  );
  const center = pointFrom<GlobalPoint>(
    (bounds[0] + bounds[2]) / 2,
    (bounds[1] + bounds[3]) / 2,
  );

  invariant(points.length >= 2, "Freepath outline must have at least 2 points");

  return points.slice(2).reduce(
    (acc, curr) => {
      acc.push(
        lineSegment<GlobalPoint>(
          acc[acc.length - 1][1],
          pointRotateRads(
            pointFrom<GlobalPoint>(curr[0] + element.x, curr[1] + element.y),
            center,
            element.angle,
          ),
        ),
      );
      return acc;
    },
    [
      lineSegment<GlobalPoint>(
        pointRotateRads(
          pointFrom<GlobalPoint>(
            points[0][0] + element.x,
            points[0][1] + element.y,
          ),
          center,
          element.angle,
        ),
        pointRotateRads(
          pointFrom<GlobalPoint>(
            points[1][0] + element.x,
            points[1][1] + element.y,
          ),
          center,
          element.angle,
        ),
      ),
    ],
  );
}
